import { Button } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import Icon from '@components/Icon/Icon';

import { getObservationConfig } from '@constants/fhir';

import { useUpdateAlertStatuses } from '@hooks/api/alerts/alert';
import { useHealthChart } from '@hooks/useHealthChart';
import { useSession } from '@hooks/useSession';

import { formatName } from '@utils/format';

import { AlertStatus } from 'generated/types';

const messages = defineMessages({
  clearAlerts: {
    id: 'healthTracker.detail.header.alerts.clearAlerts',
    defaultMessage: 'Clear All Alerts',
  },
  delete: {
    id: 'healthTracker.detail.header.alerts.delete',
    defaultMessage: 'Are you sure you want to clear alerts?',
  },
  deleteResults: {
    id: 'healthTracker.detail.header.alerts.deleteResults',
    defaultMessage:
      'This will clear all open alerts for {patient}’s {metric} metrics.',
  },
});

interface ClearAlertsButtonProps {
  disabled: boolean;
}

export default function ClearAlertsButton({
  disabled,
}: ClearAlertsButtonProps) {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();
  const { category, code } = useHealthChart();
  const signType = getObservationConfig(code);

  const [updateAlert] = useUpdateAlertStatuses();

  const handleConfirm = async () => {
    await updateAlert({
      metric: {
        vitall_patient_id: activeProfile?.vitall_patient_id ?? '',
        code,
        category,
      },
      status: AlertStatus.cleared,
    });
  };

  return (
    <ConfirmationDialog
      onConfirm={handleConfirm}
      title={formatMessage(messages.delete)}
      content={formatMessage(messages.deleteResults, {
        patient: formatName(
          activeProfile?.first_name,
          activeProfile?.last_name
        ),
        metric: signType ? formatMessage({ id: signType.messageId }) : '',
      })}
    >
      {({ handleOpen }) => (
        <Button
          color="primary"
          disabled={disabled}
          onClick={handleOpen}
          startIcon={<Icon name="triangle-exclamation" type="fas" />}
          sx={{ borderRadius: '29px', textTransform: 'capitalize' }}
          variant="outlined"
        >
          {formatMessage(messages.clearAlerts)}
        </Button>
      )}
    </ConfirmationDialog>
  );
}
